import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Phone, Sparkles, Droplets, Shirt, Truck } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { GoldThread } from '@/components/brand/GoldThread';

export default function HomePage() {
  const { t } = useTranslation();

  return (
    <>
      {/*
       * HERO
       * Editorial asymmetric layout: large serif headline on the left, brand
       * mark + tiny rotating number on the right. Generous whitespace above
       * and below. Background uses a subtle gradient mesh, not flat colour.
       */}
      <section className="relative overflow-hidden bg-bone">
        {/* Atmospheric backdrop — radial gradient + faint noise texture via SVG */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              'radial-gradient(ellipse at 80% 20%, rgba(255,180,0,0.08) 0%, transparent 50%), radial-gradient(ellipse at 10% 90%, rgba(10,10,10,0.04) 0%, transparent 60%)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-[0.025] mix-blend-multiply"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }}
        />

        <div className="container relative pt-16 pb-24 md:pt-24 md:pb-32 lg:pt-32 lg:pb-40">
          <div className="grid grid-cols-12 gap-y-12 gap-x-6">
            {/* Left: content */}
            <div className="col-span-12 lg:col-span-9 animate-fade-up">
              <div className="flex items-center gap-4 mb-6">
                <GoldThread />
                <span className="eyebrow">{t('home.eyebrow')}</span>
              </div>

              <h1
                className="font-display text-d-1 text-ink"
                style={{ fontVariationSettings: "'opsz' 144, 'wght' 500" }}
              >
                {t('home.hero_line_1')}<br />
                <span className="italic" style={{ fontVariationSettings: "'opsz' 144, 'wght' 400, 'SOFT' 100" }}>
                  {t('home.hero_line_2')}
                </span>
              </h1>

              <p className="mt-8 max-w-xl font-body text-lg md:text-xl text-charcoal leading-relaxed">
                {t('home.hero_sub')}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/book">
                  <Button variant="primary" size="lg">
                    {t('home.cta_book')}
                    <ArrowUpRight size={18} />
                  </Button>
                </Link>
                <a href="tel:+97334240115">
                  <Button variant="ghost" size="lg">
                    <Phone size={16} />
                    {t('home.cta_call')}
                  </Button>
                </a>
              </div>
            </div>

            {/* Right: small editorial marker */}
            <aside className="hidden lg:flex col-span-3 flex-col justify-between items-end">
              <div className="text-right">
                <span className="font-display text-7xl text-gold/80 leading-none" style={{ fontVariationSettings: "'opsz' 144, 'wght' 300" }}>
                  01
                </span>
                <p className="mt-3 eyebrow text-charcoal">Service</p>
              </div>
              <p className="font-body text-xs text-charcoal/70 italic max-w-[12rem] text-right leading-relaxed">
                — A modern wardrobe deserves modern care.
              </p>
            </aside>
          </div>

          {/* Trust strip */}
          <div className="mt-16 md:mt-24 pt-6 border-t border-line">
            <p className="font-body text-sm text-charcoal tracking-wide">
              {t('home.trust_strip')}
            </p>
          </div>
        </div>
      </section>

      {/*
       * SERVICES SECTION
       * Four-card grid, but rendered as compact editorial blocks rather than
       * the SaaS-template "icon-title-paragraph" pattern. Each card has a
       * number, gold thread, icon, title, brief, and link arrow.
       */}
      <section className="container py-24 md:py-32">
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-16">
          <div className="col-span-12 md:col-span-5">
            <span className="eyebrow">{t('home.services_eyebrow')}</span>
            <h2 className="mt-4 font-display text-d-3 text-ink">
              {t('home.services_title')}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <p className="font-body text-lg text-charcoal leading-relaxed pt-4 md:pt-8">
              {t('home.services_subtitle')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-line">
          <ServiceCard num="01" icon={<Droplets size={24} />} title={t('services.wash_title')}     body={t('services.wash_body')} />
          <ServiceCard num="02" icon={<Sparkles size={24} />} title={t('services.dry_title')}      body={t('services.dry_body')} />
          <ServiceCard num="03" icon={<Shirt size={24} />}    title={t('services.delicate_title')} body={t('services.delicate_body')} />
          <ServiceCard num="04" icon={<Truck size={24} />}    title={t('services.pickup_title')}   body={t('services.pickup_body')} />
        </div>
      </section>

      {/*
       * WHY US SECTION — split layout with bold statement + stats.
       */}
      <section className="bg-ink text-bone py-24 md:py-32">
        <div className="container">
          <div className="grid grid-cols-12 gap-y-12 gap-x-6">
            <div className="col-span-12 md:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <GoldThread />
                <span className="eyebrow text-bone/60">{t('home.why_eyebrow')}</span>
              </div>
              <h2 className="font-display text-d-2" style={{ fontVariationSettings: "'opsz' 144, 'wght' 500" }}>
                {t('home.why_title')}
              </h2>
              <p className="mt-8 max-w-xl font-body text-lg text-bone/75 leading-relaxed">
                {t('home.why_body')}
              </p>
              <div className="mt-10">
                <Link to="/why-us">
                  <Button variant="gold" size="md">
                    {t('nav.why_us')}
                    <ArrowUpRight size={16} />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="col-span-12 md:col-span-5 md:col-start-8 flex flex-col gap-12 md:justify-end">
              <Stat value="100%" label={t('home.stats_quality')} />
              <Stat value="5k+"  label={t('home.stats_customers')} />
              <Stat value="50k+" label={t('home.stats_garments')} />
            </div>
          </div>
        </div>
      </section>

      {/*
       * CONTACT TEASER
       * A simple, refined block. No form here — that lives on /contact.
       * Just address + phone + hours + CTA.
       */}
      <section className="container py-24 md:py-32">
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 items-start">
          <div className="col-span-12 md:col-span-6">
            <span className="eyebrow">{t('home.contact_eyebrow')}</span>
            <h2 className="mt-4 font-display text-d-3 text-ink">{t('home.contact_title')}</h2>
            <p className="mt-6 max-w-md font-body text-lg text-charcoal leading-relaxed">
              {t('home.contact_intro')}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book"><Button variant="primary">{t('home.cta_book')} <ArrowUpRight size={16} /></Button></Link>
              <Link to="/contact"><Button variant="ghost">{t('nav.contact')}</Button></Link>
            </div>
          </div>

          <div className="col-span-12 md:col-span-5 md:col-start-8 grid grid-cols-1 gap-y-6 font-body text-base text-charcoal">
            <div>
              <p className="eyebrow text-charcoal/60 mb-1">{t('contact.address_label')}</p>
              <p className="leading-relaxed">{t('contact.address_value')}</p>
            </div>
            <div>
              <p className="eyebrow text-charcoal/60 mb-1">{t('contact.phone_label')}</p>
              <a href="tel:+97334240115" className="text-ink hover:text-gold-deep">{t('contact.phone_value')}</a>
            </div>
            <div>
              <p className="eyebrow text-charcoal/60 mb-1">{t('contact.hours_label')}</p>
              <p className="leading-relaxed">{t('contact.hours_weekdays')}</p>
              <p className="leading-relaxed">{t('contact.hours_friday')}</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
function ServiceCard({ num, icon, title, body }: { num: string; icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="bg-bone p-8 md:p-10 group transition-colors hover:bg-paper">
      <div className="flex items-start justify-between mb-12">
        <span className="text-gold" aria-hidden="true">{icon}</span>
        <span className="font-display text-sm text-charcoal/60 tracking-wide">{num}</span>
      </div>
      <h3 className="font-display text-2xl md:text-3xl text-ink mb-3"
          style={{ fontVariationSettings: "'opsz' 90, 'wght' 500" }}>
        {title}
      </h3>
      <p className="font-body text-sm text-charcoal leading-relaxed">{body}</p>
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="border-s-2 border-gold ps-6">
      <p className="font-display text-5xl md:text-6xl text-bone leading-none"
         style={{ fontVariationSettings: "'opsz' 144, 'wght' 500" }}>
        {value}
      </p>
      <p className="mt-3 font-body text-sm text-bone/70 tracking-wide">{label}</p>
    </div>
  );
}
